/**
 * Hook for fetching and managing branding settings
 */
import { useCallback, useEffect, useState } from "react";
import { getCsrfToken } from "../csrf";
import { type BrandingSettings, type BrandingAssetType, DEFAULT_BRANDING } from "./brandingTypes";

type UseBrandingSettingsReturn = {
  settings: BrandingSettings;
  loading: boolean;
  uploading: BrandingAssetType | null;
  error: string | null;
  refresh: () => Promise<void>;
  uploadAsset: (assetType: BrandingAssetType, file: File) => Promise<boolean>;
  resetAsset: (assetType: BrandingAssetType) => Promise<boolean>;
  /** Promote a media-library asset into a branding slot. Errors (slot validation, demo mode) are returned, not stored. */
  promoteAsset: (
    assetType: BrandingAssetType,
    assetId: string
  ) => Promise<{ ok: boolean; message?: string }>;
};

/**
 * Cross-instance sync: every hook instance refreshes when any instance
 * changes branding, so surfaces like the sidebar logo and the Asset Management Center
 * brand panel stay current after an upload/reset/promote elsewhere.
 */
const BRANDING_CHANGED_EVENT = "branding-settings-changed";

const notifyBrandingChanged = (): void => {
  window.dispatchEvent(new CustomEvent(BRANDING_CHANGED_EVENT));
};

export const useBrandingSettings = (): UseBrandingSettingsReturn => {
  const [settings, setSettings] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<BrandingAssetType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/settings/branding");
      if (!res.ok) {
        if (res.status === 403) {
          setError("You don't have permission to view branding settings.");
        } else {
          setError("Failed to load branding settings");
        }
        setSettings(DEFAULT_BRANDING);
        return;
      }
      const data = await res.json();
      if (data.ok && data.settings) {
        setSettings(data.settings);
        // Update favicon on initial load if custom favicon is set
        if (data.settings.resolvedFaviconUrl) {
          updateDocumentFavicon(data.settings.resolvedFaviconUrl);
        }
      } else {
        setSettings(DEFAULT_BRANDING);
      }
    } catch (err) {
      setError("Failed to load branding settings");
      setSettings(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadAsset = useCallback(
    async (assetType: BrandingAssetType, file: File): Promise<boolean> => {
      setUploading(assetType);
      setError(null);
      try {
        const csrfToken = getCsrfToken();
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`/api/admin/settings/branding/${assetType}`, {
          method: "POST",
          headers: csrfToken ? { "x-csrf-token": csrfToken } : undefined,
          body: formData,
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          setError(json.error?.message ?? "Failed to upload asset");
          return false;
        }

        if (json.settings) {
          setSettings(json.settings);
        }

        // Update favicon in document if favicon was uploaded
        if (assetType === "favicon" && json.settings?.resolvedFaviconUrl) {
          updateDocumentFavicon(json.settings.resolvedFaviconUrl);
        }

        notifyBrandingChanged();
        return true;
      } catch (err) {
        setError("Failed to upload asset");
        return false;
      } finally {
        setUploading(null);
      }
    },
    []
  );

  const resetAsset = useCallback(
    async (assetType: BrandingAssetType): Promise<boolean> => {
      setUploading(assetType);
      setError(null);
      try {
        const csrfToken = getCsrfToken();
        const res = await fetch(`/api/admin/settings/branding/${assetType}`, {
          method: "DELETE",
          headers: csrfToken ? { "x-csrf-token": csrfToken } : undefined,
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          setError(json.error?.message ?? "Failed to reset asset");
          return false;
        }

        if (json.settings) {
          setSettings(json.settings);
        }

        // Update favicon if favicon was reset
        if (assetType === "favicon" && json.settings?.resolvedFaviconUrl) {
          updateDocumentFavicon(json.settings.resolvedFaviconUrl);
        }

        notifyBrandingChanged();
        return true;
      } catch (err) {
        setError("Failed to reset asset");
        return false;
      } finally {
        setUploading(null);
      }
    },
    []
  );

  const promoteAsset = useCallback(
    async (
      assetType: BrandingAssetType,
      assetId: string
    ): Promise<{ ok: boolean; message?: string }> => {
      setUploading(assetType);
      try {
        const csrfToken = getCsrfToken();
        const res = await fetch(`/api/admin/settings/branding/${assetType}/from-asset`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ assetId }),
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          return { ok: false, message: json.error?.message ?? "Failed to set brand asset" };
        }

        if (json.settings) {
          setSettings(json.settings);
        }

        if (assetType === "favicon" && json.settings?.resolvedFaviconUrl) {
          updateDocumentFavicon(json.settings.resolvedFaviconUrl);
        }

        notifyBrandingChanged();
        return { ok: true };
      } catch {
        return { ok: false, message: "Failed to set brand asset" };
      } finally {
        setUploading(null);
      }
    },
    []
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh when any other hook instance changes branding (upload/reset/promote).
  useEffect(() => {
    const handler = () => void refresh();
    window.addEventListener(BRANDING_CHANGED_EVENT, handler);
    return () => window.removeEventListener(BRANDING_CHANGED_EVENT, handler);
  }, [refresh]);

  return { settings, loading, uploading, error, refresh, uploadAsset, resetAsset, promoteAsset };
};

/**
 * Update the document's favicon link element.
 */
function updateDocumentFavicon(url: string): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) {
    existing.href = url;
  } else {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = url;
    document.head.appendChild(link);
  }
}
